import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../ui/Button';
import { STORAGE_KEYS } from '../../constants/storageKeys';
import { StorageService } from '../../services/storage';
import { getFeedback, sendFeedback } from '../../services/conversation';
import { RAG_ERROR_CODES } from '../../constants/common';
import { toast, Bounce } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const MIN_ROWS = 3;
const MAX_ROWS = 20;
const ROW_HEIGHT = 24;
const MAX_CHARACTERS = 500;

const FeedbackBox = ({
	title,
	subtitle,
	placeholder,
	closeText,
	submitText,
	conversationId,
	onOpen = (state) => {},
	autoFocusComment = false,
	defaultOpen = false,
}) => {
	const { t } = useTranslation();
	const [comment, setComment] = useState('');
	const [isPositive, setIsPositive] = useState(null);
	const [loading, setLoading] = useState(false);
	const textareaRef = useRef(null);
	const [isOpen, setIsOpen] = useState(!!defaultOpen);
	const [isCommentLimitReached, setIsCommentLimitReached] = useState(false);

	const handleSubmit = async () => {
		setLoading(true);

		const token = await StorageService.getItem(STORAGE_KEYS.AUTH_TOKEN);

		if (!token || !conversationId) {
			return;
		}

		const response = await sendFeedback(token, conversationId, isPositive, comment.trim());

		if (response.errorCode) {
			await displayErrorToast(response.errorCode);
			setLoading(false);
		} else {
			toast.success(t('result_page.feedback.success'), {
				position: 'top-center',
				autoClose: 2000,
				hideProgressBar: false,
				closeOnClick: false,
				pauseOnHover: true,
				draggable: false,
				progress: undefined,
				theme: 'colored',
				transition: Bounce,
			});
			setLoading(false);
		}
	};

	// Auto-resize textarea function
	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const scrollHeight = textareaRef.current.scrollHeight;
			const newRows = Math.min(Math.ceil(scrollHeight / ROW_HEIGHT), MAX_ROWS);
			const rows = Math.max(newRows, MIN_ROWS);
			textareaRef.current.style.height = `${rows * ROW_HEIGHT}px`;
		}
	};

	// Get quota error toast
	const displayErrorToast = async (errorCode) => {
		toast[RAG_ERROR_CODES[errorCode].toastLevel](t(`rag_error.${RAG_ERROR_CODES[errorCode].langKey}`), {
			position: 'top-center',
			autoClose: false,
			hideProgressBar: false,
			closeOnClick: false,
			pauseOnHover: true,
			draggable: false,
			progress: undefined,
			theme: 'colored',
			transition: Bounce,
		});
	};

	// Clear feedback state
	const clearFeedback = () => {
		setIsPositive(null);
		setComment('');
		setIsCommentLimitReached(false);
	};

	// Resize on component mount and when message changes
	useEffect(() => {
		autoResizeTextarea();

		if (comment.length > MAX_CHARACTERS) {
			setIsCommentLimitReached(true);
		} else {
			setIsCommentLimitReached(false);
		}
	}, [comment]);

	// Fetch feedback
	useEffect(() => {
		if (!conversationId) {
			return;
		}

		const fetchFeedback = async () => {
			const token = await StorageService.getItem(STORAGE_KEYS.AUTH_TOKEN);

			if (!token || !conversationId) {
				return;
			}

			const feedbackResponse = await getFeedback(token, conversationId);

			if (!feedbackResponse || feedbackResponse.errorCode) {
				// If feedback not found, don't display error toast
				if (feedbackResponse.errorCode !== 10) {
					await displayErrorToast(feedbackResponse.errorCode);
				}
				clearFeedback();
				setIsOpen(false);
				return;
			}

			setIsPositive(feedbackResponse.feedback.positive);
			setComment(feedbackResponse.feedback.comment);
			setIsOpen(true);
		};

		fetchFeedback();
	}, [conversationId]);

	useEffect(() => {
		if (isOpen) {
			onOpen({ isPositive });
			if (autoFocusComment && textareaRef.current) {
				textareaRef.current.focus();
			}
		}
	}, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleVote = (positive) => {
		setIsPositive(positive);
		if (!isOpen) setIsOpen(true);
	};

	return (
		<div className='w-full mb-4'>
			{/* Collapsed header (always visible) */}
			<div
				className={`flex items-center justify-between border border-none bg-primary px-3 py-1.5 ${
					isOpen ? 'rounded-t-md' : 'rounded-md'
				}`}
				aria-expanded={isOpen}
			>
				<span className='text-xs text-on-primary tracking-wide font-medium select-none'>
					{title.toUpperCase()}
				</span>

				<div className='flex items-center gap-2'>
					<button
						type='button'
						onClick={() => handleVote(true)}
						aria-label='Up-vote'
						className={`rounded-md border border-transparent bg-primary px-1.5 py-0.5 text-blue-700 font-semibold hover:opacity-90 hover:border-white cursor-pointer ${
							isPositive === true
								? 'outline-none ring-1 ring-white ring-offset-1 ring-offset-white-600'
								: ''
						}`}
					>
						<i className='ri-thumb-up-fill text-xl text-white' />
					</button>
					<button
						type='button'
						onClick={() => handleVote(false)}
						aria-label='Down-vote'
						className={`rounded-md border border-transparent bg-primary px-1.5 py-0.5 text-blue-700 font-semibold hover:opacity-90 hover:border-white cursor-pointer ${
							isPositive === false
								? 'outline-none ring-1 ring-white ring-offset-1 ring-offset-white-600'
								: ''
						}`}
					>
						<i className='ri-thumb-down-fill text-xl text-white' />
					</button>
				</div>
			</div>

			{/* Expanded content */}
			{isOpen && (
				<div className='rounded-b-md border border-t-0 border-tertiary bg-on-primary p-4'>
					<label
						className='block text-sm font-medium text-on-tertiary-active mb-2'
						htmlFor='feedback-comment'
					>
						{subtitle}
					</label>
					<textarea
						id='feedback-comment'
						ref={textareaRef}
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						rows={4}
						className='w-full rounded-md bg-white  pl-2 pr-2 pt-1 pb-1 text-black focus-visible:outline-none'
						style={{
							boxShadow: 'inset 0 0 0 2px #999', // inset shadow: 3px, #999
						}}
						placeholder={placeholder}
						maxLength={MAX_CHARACTERS}
					/>
					<span
						className={`pl-2 text-sm text-on-tertiary-active ${
							isCommentLimitReached ? 'text-red-500' : ''
						}`}
					>
						{`${MAX_CHARACTERS - comment.length} ${t('result_page.feedback.characters_remaining')}`}
					</span>
					<div className='mt-2 flex items-center justify-end gap-2'>
						<Button variant='primary' size='medium' type='line' onClick={() => setIsOpen(false)}>
							{closeText}
						</Button>
						<Button
							variant='primary'
							size='medium'
							type='solid'
							onClick={handleSubmit}
							disabled={loading || isCommentLimitReached}
						>
							{submitText}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

FeedbackBox.propTypes = {
	title: PropTypes.string,
	subtitle: PropTypes.string,
	placeholder: PropTypes.string,
	closeText: PropTypes.string,
	submitText: PropTypes.string,
	onOpen: PropTypes.func,
	onSubmit: PropTypes.func,
	autoFocusComment: PropTypes.bool,
	defaultOpen: PropTypes.bool,
	conversationId: PropTypes.number.isRequired,
};

export default FeedbackBox;
